import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { GraduationCap, Target, Building2, Briefcase } from "lucide-react";

/**
 * Tiny pill-row that surfaces the four audiences on every public page so
 * visitors can hop between Students / Working Professionals / Institutions
 * / Companies without going back to the homepage.
 *
 * Renders only on the public marketing surface — `PublicNavBar.jsx`
 * is the host. Hidden on dashboards, signup, and legal pages where the
 * audience context isn't relevant.
 *
 * Theming notes:
 *   • Container + chips consume the ``--surface``, ``--rule``, ``--muted``,
 *     ``--ink``, ``--signal`` and ``--signal-tint`` CSS vars so the row
 *     inherits the correct look in BOTH light and dark themes (previously
 *     the row was hard-coded to ``bg-white/60`` which vanished in dark).
 *   • Active state uses a single unified blue tint — dropping the previous
 *     per-audience colour palette because those pale ``bg-*-50`` shades
 *     were invisible on the dark navy dark-mode paper. One accent → looks
 *     professional and uniform across themes as requested.
 */
const AUDIENCES = [
  { to: "/for-students",      label: "Students",              Icon: GraduationCap },
  { to: "/for-professionals", label: "Working Professionals", Icon: Target        },
  { to: "/for-institutions",  label: "Institutions",          Icon: Building2     },
  { to: "/for-companies",     label: "Companies",             Icon: Briefcase     },
];

const HIDE_ON = [
  /^\/signin\b/, /^\/signup\b/, /^\/login\b/, /^\/admin/, /^\/student\//,
  /^\/company\//, /^\/institute\//, /^\/legal/, /^\/onboarding/, /^\/assessments?\//,
  /^\/verify-/, /^\/privacy\b/, /^\/terms\b/,
];

export default function AudienceSwitcher() {
  const { pathname } = useLocation();
  if (HIDE_ON.some((re) => re.test(pathname))) return null;

  return (
    <div
      className="audience-switcher-bar sticky top-[60px] z-30 backdrop-blur-sm"
      data-testid="audience-switcher"
      role="navigation"
      aria-label="Choose audience"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
        <span className="audience-switcher-label text-[10px] uppercase tracking-widest font-bold pr-1 hidden sm:inline shrink-0">
          For
        </span>
        {AUDIENCES.map(({ to, label, Icon }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`audience-switch-${to.split("/").pop()}`}
            className={({ isActive }) =>
              [
                "audience-switcher-chip inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium whitespace-nowrap shrink-0 transition",
                isActive ? "is-active" : "",
              ].join(" ").trim()
            }
          >
            <Icon size={12} aria-hidden="true" />
            <span>{label}</span>
          </NavLink>
        ))}
      </div>
    </div>
  );
}

